import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Users } from 'lucide-react';
import { fundingAuthAPI, getIpeWorkspaceMode, setIpeWorkspaceMode, isRequestAborted } from '@/utils/api';

/** 切换成功后短暂冷却，避免连点打满连接池 */
const SWITCH_COOLDOWN_MS = 500;

/**
 * 个人 / 团队工作区切换。
 * - 仅当账号可使用团队工作区（已加入团队且团队会员有效）时展示
 * - compact：列表页筛选条用「个人 | 团队」分段样式
 */
export default function IpeWorkspaceSwitcher({
  showToast,
  className = '',
  disabled = false,
  disabledTitle,
  variant = 'default',
}) {
  const { t } = useTranslation();
  const compact = variant === 'compact';
  const [mode, setMode] = useState(() => getIpeWorkspaceMode());
  const [teamAvailable, setTeamAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const switchingRef = useRef(false);
  const desiredModeRef = useRef(null);
  const mountAbortRef = useRef(null);

  const syncFromServer = useCallback(async (signal) => {
    try {
      const data = await fundingAuthAPI.getAccountCenter({ signal });
      const available = Boolean(data?.teamWorkspaceAvailable);
      const serverMode = data?.workspaceMode || 'personal';
      const effectiveMode = available
        ? (data?.workspaceModeEffective || serverMode || 'personal')
        : 'personal';
      setTeamAvailable(available);
      setMode(effectiveMode);
      // 同值短路：不重复广播
      setIpeWorkspaceMode(effectiveMode);
    } catch (err) {
      if (isRequestAborted(err)) return;
      setTeamAvailable(false);
      setMode('personal');
      setIpeWorkspaceMode('personal');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    mountAbortRef.current = controller;
    syncFromServer(controller.signal);
    const onChanged = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setMode(next);
      }
    };
    // 仅订阅 mode-changed；勿在 refresh 时再打 account（避免切换风暴）
    window.addEventListener('ipe:workspace-mode-changed', onChanged);
    window.addEventListener('ipe-workspace-mode-changed', onChanged);
    return () => {
      controller.abort();
      window.removeEventListener('ipe:workspace-mode-changed', onChanged);
      window.removeEventListener('ipe-workspace-mode-changed', onChanged);
    };
  }, [syncFromServer]);

  const runSwitchLoop = useCallback(async () => {
    if (switchingRef.current) return;
    switchingRef.current = true;
    setSwitching(true);
    let lastApplied = null;
    try {
      while (desiredModeRef.current) {
        const target = desiredModeRef.current;
        desiredModeRef.current = null;
        if (target !== 'personal' && target !== 'team') break;
        if (target === getIpeWorkspaceMode()) {
          setMode(target);
          lastApplied = target;
          continue;
        }
        await fundingAuthAPI.updateWorkspaceMode(target);
        setMode(getIpeWorkspaceMode());
        lastApplied = getIpeWorkspaceMode();
      }
      if (lastApplied) {
        showToast?.(lastApplied === 'team' ? t('switchedToTeam') : t('switchedToPersonal'), 'success');
      }
      await new Promise((resolve) => setTimeout(resolve, SWITCH_COOLDOWN_MS));
    } catch (err) {
      if (!isRequestAborted(err)) {
        showToast?.(err?.message || t('switchModeFailed'), 'error');
      }
    } finally {
      switchingRef.current = false;
      setSwitching(false);
      if (desiredModeRef.current && desiredModeRef.current !== getIpeWorkspaceMode()) {
        void runSwitchLoop();
      }
    }
  }, [showToast, t]);

  const handleSwitch = (nextMode) => {
    if (disabled) return;
    if (nextMode !== 'personal' && nextMode !== 'team') return;
    if (nextMode === mode && !switchingRef.current) return;
    desiredModeRef.current = nextMode;
    void runSwitchLoop();
  };

  if (loading || !teamAvailable) {
    return null;
  }

  return (
    <div
      className={`ipe-workspace-switcher${compact ? ' ipe-workspace-switcher--compact' : ''}${disabled ? ' ipe-workspace-switcher--locked' : ''}${className ? ` ${className}` : ''}`}
      role="group"
      aria-label={t('workspaceSwitchAria')}
      title={
        disabled
          ? (disabledTitle || t('workspaceSwitchLocked'))
          : (switching ? t('workspaceSwitching') : undefined)
      }
    >
      <button
        type="button"
        className={`ipe-workspace-switcher__btn${mode === 'personal' ? ' is-active' : ''}`}
        onClick={() => handleSwitch('personal')}
        disabled={switching || disabled}
        aria-pressed={mode === 'personal'}
        aria-disabled={disabled || undefined}
      >
        {compact ? null : <User size={14} strokeWidth={2} aria-hidden="true" />}
        {compact ? t('personalShort') : t('personalMode')}
      </button>
      <button
        type="button"
        className={`ipe-workspace-switcher__btn${mode === 'team' ? ' is-active' : ''}`}
        onClick={() => handleSwitch('team')}
        disabled={switching || disabled}
        aria-pressed={mode === 'team'}
        aria-disabled={disabled || undefined}
      >
        {compact ? null : <Users size={14} strokeWidth={2} aria-hidden="true" />}
        {compact ? t('teamShort') : t('teamMode')}
      </button>
    </div>
  );
}
